import { createContext, useCallback, useContext, useEffect, useId, useRef, useSyncExternalStore, } from 'react';
export function createOverlayCoordinator() {
    let openOverlayId = null;
    const listenersByOverlayId = new Map();
    const notifyOverlay = (overlayId) => {
        if (overlayId == null)
            return;
        listenersByOverlayId.get(overlayId)?.forEach((listener) => listener());
    };
    return {
        getOpenOverlayId: () => openOverlayId,
        setOpenOverlayId: (update) => {
            const nextOverlayId = typeof update === 'function' ? update(openOverlayId) : update;
            if (nextOverlayId === openOverlayId)
                return;
            const previousOverlayId = openOverlayId;
            openOverlayId = nextOverlayId;
            notifyOverlay(previousOverlayId);
            notifyOverlay(nextOverlayId);
        },
        subscribe: (overlayId, listener) => {
            const listeners = listenersByOverlayId.get(overlayId) ?? new Set();
            listeners.add(listener);
            listenersByOverlayId.set(overlayId, listeners);
            return () => {
                listeners.delete(listener);
                if (listeners.size === 0)
                    listenersByOverlayId.delete(overlayId);
            };
        },
    };
}
// Provider-less roots share one fallback coordinator instead of creating isolated stores.
export const OverlayCoordinatorContext = createContext(createOverlayCoordinator());
OverlayCoordinatorContext.displayName = 'AetherOverlayCoordinatorContext';
function useOverlayCoordinator() {
    return useContext(OverlayCoordinatorContext);
}
/**
 * Coordinates one manually controlled overlay with other Aether overlays.
 */
export function useCoordinatedOverlay() {
    const overlayId = useId();
    const overlayCoordinator = useOverlayCoordinator();
    const subscribe = useCallback((listener) => overlayCoordinator.subscribe(overlayId, listener), [overlayCoordinator, overlayId]);
    const getSnapshot = useCallback(() => overlayCoordinator.getOpenOverlayId() === overlayId, [overlayCoordinator, overlayId]);
    const isOpen = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
    const open = useCallback(() => {
        overlayCoordinator.setOpenOverlayId(overlayId);
    }, [overlayCoordinator, overlayId]);
    const close = useCallback(() => {
        overlayCoordinator.setOpenOverlayId((currentId) => currentId === overlayId ? null : currentId);
    }, [overlayCoordinator, overlayId]);
    return {
        isOpen,
        open,
        close,
    };
}
/**
 * Hook to manage overlay (popover/tooltip) state and ensure only one overlay is open at a time.
 *
 * Supports both controlled and uncontrolled modes:
 * - Uncontrolled: Manages internal state and automatically closes when another overlay opens
 * - Controlled: Delegates state management to parent component
 *
 * In both modes `onOpenChange` is notified of every open state change.
 */
export function useOverlay({ open, onOpenChange }) {
    const { isOpen: internalOpen, open: openCoordinatedOverlay, close: closeCoordinatedOverlay, } = useCoordinatedOverlay();
    const onOpenChangeRef = useRef(onOpenChange);
    const notifiedStateRef = useRef(null);
    onOpenChangeRef.current = onOpenChange;
    // Controlled overlays use the provided state. Uncontrolled overlays derive their state from
    // whether they currently own the shared coordinator.
    const isControlled = open !== undefined;
    const isOpen = isControlled ? open : internalOpen;
    const previousInternalOpenRef = useRef(internalOpen);
    // Notify an uncontrolled overlay when another overlay takes ownership. Changes initiated through
    // handleOpenChange have already notified the caller and are skipped here.
    useEffect(() => {
        const previousInternalOpen = previousInternalOpenRef.current;
        previousInternalOpenRef.current = internalOpen;
        if (isControlled || previousInternalOpen === internalOpen)
            return;
        if (notifiedStateRef.current === internalOpen) {
            notifiedStateRef.current = null;
            return;
        }
        notifiedStateRef.current = null;
        onOpenChangeRef.current?.(internalOpen);
    }, [internalOpen, isControlled]);
    const handleOpenChange = useCallback((newOpen) => {
        if (!isControlled) {
            // Uncontrolled overlays claim or release shared ownership in the coordinator.
            notifiedStateRef.current = newOpen;
            if (newOpen)
                openCoordinatedOverlay();
            else
                closeCoordinatedOverlay();
        }
        onOpenChangeRef.current?.(newOpen);
    }, [closeCoordinatedOverlay, isControlled, openCoordinatedOverlay]);
    return {
        isOpen,
        handleOpenChange,
    };
}
//# sourceMappingURL=useOverlay.js.map